import { IDocumentDetails, IMailInfo, IMultipleMailInfo, IMultipleMailSaveResponse } from "../../@types/models";
import fetchClient from "./fetchClient";
import { getEnvironment360BizURL, getFetchApiOptions, ApiConfig } from '../configs/appConfig';
import { getHeaders } from "./dataService";
import { getMsalAccessToken } from "./msalUtil";
import { getMailDetails as getMailDetailsInternal } from "./dataServiceOutlookMailDetails";
import {
    getCurrentInternetMessageId,
    getSelectedMessageIds,
    isValidInternetMessageId,
    normalizeInternetMessageId,
} from "./OutlookMessageIdUtil";
import { t } from "i18next";

export const MSAL_GRAPH_Scope: string[] = ["Mail.Read", "User.Read", "openid", "profile", "offline_access", "email"];

export const getMsalGraphScopes = async (): Promise<string[]> => {
    const resolvedTargetInbox = await getTargetInbox();
    const scopes = [...MSAL_GRAPH_Scope];
    if (resolvedTargetInbox?.trim()) {
        scopes.push("Mail.Read.Shared");
    }

    return scopes;
};
type MultipleMailSaveRequest = { accessToken: string; itemIds: string[]; targetInbox?: string | null };
export type MultipleMailArchiveInfoMap = Record<string, IDocumentDetails[]>;
interface ISharedPropertiesCapableItem { getSharedPropertiesAsync?: (callback: (asyncResult: Office.AsyncResult<Office.SharedProperties>) => void) => void; }

export const getTargetInbox = async (): Promise<string | null> => {
    const mailboxItem = Office.context.mailbox.item as ISharedPropertiesCapableItem | null | undefined;
    const getSharedPropertiesAsync = mailboxItem?.getSharedPropertiesAsync;
    if (!getSharedPropertiesAsync) {
        return null;
    }

    return await new Promise((resolve) => {
        try {
            getSharedPropertiesAsync((asyncResult: Office.AsyncResult<Office.SharedProperties>) => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    resolve(null);
                    return;
                }

                const targetInbox = asyncResult.value?.targetMailbox?.trim();
                resolve(targetInbox || null);
            });
        } catch {
            resolve(null);
        }
    });
};

const _findAndGetMailInfo = async (
    s: (r: IDocumentDetails | null) => void,
    e: (err: string) => void,
    d: string | null,
    selectedItemId?: string | null
) => {
    const ti = await getTargetInbox();
    const t = await getMsalAccessToken(await getMsalGraphScopes());
    if (!t) { e("No token"); return; }
    const selectedInternetMessageId = normalizeInternetMessageId(selectedItemId);
    const itemId = selectedInternetMessageId || await getCurrentInternetMessageId();
    if (!itemId) {
        e("Cannot retrieve mail ID for selected email");
        return;
    }
    void getMailDetailsInternal({ accessToken: t, itemId, docRecno: d, targetInbox: ti ?? null }, s, e);
};
export { _findAndGetMailInfo as findAndGetMailInfo };

export const archiveMail = async (successCb: (recno: string | null) => void, errorCb: (err: string) => void) => {
    const targetInbox = await getTargetInbox();
    const token = await getMsalAccessToken(await getMsalGraphScopes());
    if (!token) {
        errorCb("Failed to acquire access token");
        return;
    }
    const itemId = await getCurrentInternetMessageId();
    if (!itemId) {
        errorCb("Cannot retrieve mail ID for selected email");
        return;
    }
    const mailInfo: IMailInfo = { accessToken: token, itemId, targetInbox: targetInbox ?? null };
    void archiveMailInfo(mailInfo, successCb, errorCb);
};

export const saveMultipleEmails = async (
    successCb: (results: IMultipleMailSaveResponse) => void,
    errorCb: (err: string) => void,
    itemIdsToArchive?: string[]
) => {
    const targetInbox = await getTargetInbox();
    const token = await getMsalAccessToken(await getMsalGraphScopes());
    if (!token) {
        errorCb("Failed to acquire access token");
        return;
    }

    // Normalize and deduplicate itemIds, validating that they are in proper internet message ID format
    const normalizedItemIds = Array.from(
        new Set(
            (itemIdsToArchive ?? [])
                .map((itemId) => {
                    const trimmed = itemId?.trim() ?? '';
                    return isValidInternetMessageId(trimmed) ? trimmed : '';
                })
                .filter((itemId): itemId is string => itemId.length > 0)
        )
    );

    const itemIds = normalizedItemIds.length > 0
        ? normalizedItemIds
        : await getSelectedMessageIds(token, targetInbox);

    if (itemIds.length === 0) {
        errorCb(t("Cannot retrieve mail IDs for selected emails"));
        return;
    }

    const mailInfo: MultipleMailSaveRequest = { accessToken: token, itemIds, targetInbox: targetInbox ?? null };
    void saveMultipleEmailInfo(mailInfo, successCb, errorCb);
};

export const getMultipleArchivedMailInfo = async (
    successCb: (resData: MultipleMailArchiveInfoMap) => void,
    errorCb: (err: string) => void,
    itemIdsToLoad?: string[]
) => {
    const targetInbox = await getTargetInbox();
    const token = await getMsalAccessToken(await getMsalGraphScopes());
    if (!token) {
        errorCb("Failed to acquire access token");
        return;
    }
    const providedItemIds = Array.from(
        new Set(
            (itemIdsToLoad ?? [])
                .map((itemId) => normalizeInternetMessageId(itemId))
                .filter((itemId): itemId is string => Boolean(itemId))
        )
    );
    const itemIds = providedItemIds.length > 0
        ? providedItemIds
        : await getSelectedMessageIds(token, targetInbox);
    if (itemIds.length === 0) {
        errorCb(t("Cannot retrieve mail IDs for selected emails"));
        return;
    }
    const mailInfo: IMultipleMailInfo = { accessToken: token, itemIds, targetInbox: targetInbox ?? null };
    void loadMultipleMailDetails(mailInfo, successCb, errorCb);
};

const archiveMailInfo = async (mailInfo: IMailInfo, sCB: (res: string) => void, eCB: (err: string) => void) => {
    const headers = await getHeaders();
    const targetInbox = mailInfo.targetInbox === undefined ? await getTargetInbox() : mailInfo.targetInbox;
    const requestBody: IMailInfo = { ...mailInfo, targetInbox };
    try {
        const response = await fetchClient(getEnvironment360BizURL() + ApiConfig.SaveMailUsingWizard, {
            body: JSON.stringify(requestBody),
            headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as string;
        sCB(response);
    } catch (err) {
        eCB("Error archiving mail: " + String(err));
    }
};

const saveMultipleEmailInfo = async (mailInfo: MultipleMailSaveRequest, sCB: (results: IMultipleMailSaveResponse) => void, eCB: (err: string) => void) => {
    const headers = await getHeaders();
    const targetInbox: string | null = mailInfo.targetInbox === undefined ? await getTargetInbox() : mailInfo.targetInbox;
    const requestBody: MultipleMailSaveRequest = { accessToken: mailInfo.accessToken, itemIds: mailInfo.itemIds, targetInbox };
    try {
        const response = await fetchClient(getEnvironment360BizURL() + ApiConfig.SaveMultipleEmails, {
            body: JSON.stringify(requestBody),
            headers,
            method: "POST",
            ...getFetchApiOptions()
        });
        if (!Array.isArray(response)) {
            eCB("Error archiving mail: Invalid response format");
            return;
        }
        sCB(response as IMultipleMailSaveResponse);
    } catch (err) {
        eCB("Error archiving mail: " + String(err));
    }
};

const loadMultipleMailDetails = async (mailInfo: IMultipleMailInfo, sCB: (resData: MultipleMailArchiveInfoMap) => void, eCB: (err: string) => void) => {
    const headers = await getHeaders();
    const targetInbox = mailInfo.targetInbox === undefined ? await getTargetInbox() : mailInfo.targetInbox;
    const requestBody: IMultipleMailInfo = { ...mailInfo, targetInbox };
    try {
        const response = await fetchClient(getEnvironment360BizURL() + ApiConfig.GetMultipleArchivedMailInfo, {
            body: JSON.stringify(requestBody),
            headers,
            method: "POST",
            ...getFetchApiOptions()
        });
        if (typeof response !== 'object' || response === null || Array.isArray(response)) {
            eCB("Error archiving mail: Invalid response format");
            return;
        }
        const responseData = response as MultipleMailArchiveInfoMap;
        sCB(responseData);
    } catch (err) {
        eCB("Error archiving mail: " + String(err));
    }
};

export { getMailDetails } from "./dataServiceOutlookMailDetails";
export { getSelectedMessageIdsResult, getSelectedMessageIds, GetItemRestId } from "./OutlookMessageIdUtil";
export type { GetSelectedMessageIdsResult } from "./OutlookMessageIdUtil";

export const GetRestUrl = (): string => Office.context.mailbox.restUrl + '/v2.0';
